import type {
  AdminBranch,
  AdminClosure,
  ClosureKind,
  DaySchedule,
  HoursInterval,
  IsoWeekday,
  LocalizedPair,
  WeeklySchedule,
} from '@diora/contracts';
import { editablePair } from '@diora/contracts';
import { formatClock, formatDate, formatWeekday, ISO_WEEKDAYS } from '@diora/contracts';
import { useQuery } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import { PageHeader } from '@/components/app-shell';
import { OptionalBilingualField } from '@/components/bilingual';
import { IntervalRows } from '@/components/hours';
import { Alert, Badge, Button, Card, EmptyState, Select, SkeletonRows } from '@/components/ui';
import { apiGet, apiPost, apiPut, useAdminMutation } from '@/hooks/admin';
import { qk } from '@/lib/query';
import { useAuth } from '@/providers/auth';
import { useLocale } from '@/providers/locale';
import { BRANCHES_LIST, BRANCHES_URL, OpenBadge } from './branches';

interface PreviewDay {
  date: string;
  closed: boolean;
  intervals: HoursInterval[];
  overriddenBy: { kind: ClosureKind; reason: LocalizedPair | null } | null;
}

interface Preview {
  timezone: string | null;
  scheduleReadable: boolean;
  days: PreviewDay[];
}

const EMPTY_WEEK: WeeklySchedule = {
  1: { kind: 'closed' },
  2: { kind: 'closed' },
  3: { kind: 'closed' },
  4: { kind: 'closed' },
  5: { kind: 'closed' },
  6: { kind: 'closed' },
  7: { kind: 'closed' },
};

export function BranchDetailPage() {
  const { t } = useTranslation();
  const { id = '' } = useParams();
  const { can } = useAuth();
  const canEdit = can('OWNER', 'EDITOR');

  const branch = useQuery({
    queryKey: qk.branch(id),
    queryFn: () => apiGet<AdminBranch>(`${BRANCHES_URL}/${id}`),
    enabled: Boolean(id),
  });

  const preview = useQuery({
    queryKey: qk.branchPreview(id),
    queryFn: () => apiGet<Preview>(`${BRANCHES_URL}/${id}/preview`, { days: 7 }),
    enabled: Boolean(id),
  });

  const row = branch.data?.data;

  if (branch.isLoading) return <SkeletonRows rows={6} />;
  if (!row) return <Alert tone="danger">{t('common.somethingBroke')}</Alert>;

  return (
    <>
      <PageHeader
        width="narrow"
        title={
          <span className="flex flex-wrap items-center gap-2">
            <span lang="ar" dir="rtl">{row.name.ar || row.name.en}</span>
            <OpenBadge isOpenNow={row.isOpenNow} />
          </span>
        }
        description={row.timezone ?? t('hours.notConfiguredHint')}
        actions={
          <>
            {canEdit && (
              <Link
                to={`${BRANCHES_LIST}/${id}/edit`}
                className="dio-btn dio-btn--secondary dio-btn--sm"
              >
                {t('branches.editTitle')}
              </Link>
            )}
            <Link to={BRANCHES_LIST} className="dio-btn dio-btn--ghost dio-btn--sm">
              {t('common.back')}
            </Link>
          </>
        }
      />

      <div className="grid gap-4 lg:grid-cols-2">
        <HoursEditor branch={row} canEdit={canEdit} />
        <div className="flex flex-col gap-4">
          <PreviewCard preview={preview.data?.data} loading={preview.isLoading} />
          <ClosuresCard branchId={id} canEdit={canEdit} />
        </div>
      </div>
    </>
  );
}

// ------------------------------------------------------------------- hours

function HoursEditor({ branch, canEdit }: { branch: AdminBranch; canEdit: boolean }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const [week, setWeek] = useState<WeeklySchedule>(branch.hoursSchedule ?? EMPTY_WEEK);
  const [note, setNote] = useState<LocalizedPair>(editablePair(branch.hoursNote));

  const save = useAdminMutation(
    () =>
      apiPut(`${BRANCHES_URL}/${branch.id}/hours`, {
        expectedVersion: branch.version,
        schedule: week,
        hoursNoteAr: note.ar,
        hoursNoteEn: note.en,
      }),
    {
      successMessage: t('common.saved'),
      invalidate: [['branches'], ['branch', branch.id], ['branch-preview', branch.id]],
    },
  );

  function setDay(day: IsoWeekday, next: DaySchedule) {
    setWeek((current) => ({ ...current, [day]: next }));
  }

  return (
    <Card title={t('hours.title')} description={t('hours.subtitle')}>
      {!branch.hoursReadable && <Alert tone="danger">{t('branches.hoursUnreadable')}</Alert>}

      <div className="flex flex-col gap-4">
        {ISO_WEEKDAYS.map((day) => {
          const schedule = week[day] ?? { kind: 'closed' };
          return (
            <div key={day} className="border-b border-border-decor pb-3">
              <div className="flex flex-wrap items-center gap-3">
                <span className="font-medium" style={{ minInlineSize: '6rem' }}>
                  {formatWeekday(day, locale)}
                </span>

                <Select
                  aria-label={formatWeekday(day, locale)}
                  disabled={!canEdit}
                  value={schedule.kind}
                  onChange={(event) => {
                    const kind = event.target.value as DaySchedule['kind'];
                    setDay(
                      day,
                      kind === 'intervals'
                        ? { kind, intervals: [{ opensAt: '09:00', closesAt: '22:00', closesNextDay: false }] }
                        : { kind },
                    );
                  }}
                >
                  <option value="closed">{t('hours.closed')}</option>
                  <option value="open24">{t('hours.open24')}</option>
                  <option value="intervals">{t('hours.intervals')}</option>
                </Select>
              </div>

              {schedule.kind === 'intervals' && (
                <div className="mt-2 flex flex-col gap-2" style={{ paddingInlineStart: 'var(--space-6)' }}>
                  <IntervalRows
                    intervals={schedule.intervals}
                    disabled={!canEdit}
                    removable
                    onChange={(intervals) => setDay(day, { kind: 'intervals', intervals })}
                  />
                </div>
              )}
            </div>
          );
        })}

        <OptionalBilingualField
          label={t('branches.hoursNote')}
          value={note}
          onChange={setNote}
          maxLength={200}
          disabled={!canEdit}
        />

        {canEdit && (
          <Button variant="primary" loading={save.isPending} onClick={() => save.mutate(undefined)}>
            {t('hours.saveWeek')}
          </Button>
        )}
      </div>
    </Card>
  );
}

// ----------------------------------------------------------------- preview

function PreviewCard({ preview, loading }: { preview?: Preview; loading: boolean }) {
  const { t } = useTranslation();
  const { locale } = useLocale();

  return (
    <Card title={t('hours.previewTitle')} description={t('hours.previewHint')}>
      {loading ? (
        <SkeletonRows rows={4} />
      ) : !preview || preview.days.length === 0 ? (
        <EmptyState title={t('hours.notConfigured')} hint={t('hours.notConfiguredHint')} />
      ) : (
        <ul className="dio-hours">
          {preview.days.map((day) => (
            <li key={day.date} className="dio-hours__row">
              <span className="dio-hours__day">
                {formatDate(day.date, locale, preview.timezone ?? undefined)}
              </span>
              <span className="dio-hours__time tabular">
                {day.closed ? (
                  <span className="dio-hours__closed">{t('hours.closed')}</span>
                ) : (
                  day.intervals
                    .map(
                      (interval) =>
                        `${formatClock(interval.opensAt, locale)} – ${formatClock(interval.closesAt, locale)}${
                          interval.closesNextDay ? '⁺' : ''
                        }`,
                    )
                    .join(' · ')
                )}
              </span>
              {day.overriddenBy && (
                <Badge tone="info">
                  {t(`closures.${day.overriddenBy.kind}`)}
                  {day.overriddenBy.reason?.[locale] ? ` · ${day.overriddenBy.reason[locale]}` : ''}
                </Badge>
              )}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------- closures

function ClosuresCard({ branchId, canEdit }: { branchId: string; canEdit: boolean }) {
  const { t } = useTranslation();
  const { locale } = useLocale();

  const query = useQuery({
    queryKey: qk.closures(branchId),
    queryFn: () => apiGet<AdminClosure[]>(`${BRANCHES_URL}/${branchId}/closures`),
  });

  const invalidate = [qk.closures(branchId), ['branch-preview', branchId], ['branches']];

  const archive = useAdminMutation(
    (closure: AdminClosure) =>
      apiPost(`${BRANCHES_URL}/${branchId}/closures/${closure.id}/archive`, {
        expectedVersion: closure.version,
      }),
    { successMessage: t('publish.archived'), invalidate },
  );

  const rows = query.data?.data ?? [];

  return (
    <Card
      title={t('closures.title')}
      description={t('closures.subtitle')}
      actions={
        canEdit && (
          <Link
            to={`${BRANCHES_LIST}/${branchId}/closures/new`}
            className="dio-btn dio-btn--secondary dio-btn--sm"
          >
            <Plus className="dio-icon" aria-hidden="true" />
            {t('closures.newTitle')}
          </Link>
        )
      }
    >
      {query.isLoading ? (
        <SkeletonRows rows={3} />
      ) : rows.length === 0 ? (
        <EmptyState title={t('closures.empty')} hint={t('closures.appliesNote')} />
      ) : (
        <ul className="flex flex-col gap-3">
          {rows.map((closure) => (
            <li key={closure.id} className="flex flex-wrap items-center gap-2">
              <span className="tabular">
                {formatDate(closure.startDate, locale)}
                {closure.startDate !== closure.endDate && ` – ${formatDate(closure.endDate, locale)}`}
              </span>
              <Badge tone={closure.kind === 'CLOSED' ? 'danger' : 'warning'}>
                {t(`closures.${closure.kind}`)}
              </Badge>
              {closure.reason?.[locale] && <span className="dio-meta">{closure.reason[locale]}</span>}

              <span className="flex-1" />
              {canEdit && (
                <>
                  <Link
                    to={`${BRANCHES_LIST}/${branchId}/closures/${closure.id}`}
                    className="dio-btn dio-btn--secondary dio-btn--sm"
                  >
                    {t('common.edit')}
                  </Link>
                  <Button
                    size="sm"
                    variant="ghost"
                    loading={archive.isPending}
                    onClick={() => archive.mutate(closure)}
                  >
                    {t('publish.archive')}
                  </Button>
                </>
              )}
            </li>
          ))}
        </ul>
      )}

      <p className="dio-hint mt-3">{t('closures.datesInclusive')}</p>
    </Card>
  );
}
